<template>
  <div class="success-page">
    <div class="container">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-card" data-aos="fade-up">
        <div class="spinner"></div>
        <p>Loading your registration details...</p>
      </div>

      <div v-else class="success-card" data-aos="zoom-in">
        <!-- Success Icon -->
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>

        <h1 class="success-title">You're All Set! 🎨</h1>
        <p class="success-message">
          Your workshop registration is confirmed. We can't wait to see you there!
        </p>

        <!-- Workshop Info Banner -->
        <div v-if="workshopDetail" class="workshop-banner">
          <div class="workshop-banner-title">
            <i class="fas fa-palette"></i>
            {{ workshopDetail.title }}
          </div>
          <div class="workshop-banner-meta">
            <span>
              <i class="fas fa-calendar-alt"></i>
              {{ formatDate(workshopDetail.date) }}
            </span>
            <span>
              <i class="fas fa-clock"></i>
              {{ workshopDetail.time }}
            </span>
          </div>
          <div class="workshop-banner-address">
            <i class="fas fa-map-marker-alt"></i>
            {{ workshopDetail.address }}
          </div>
        </div>

        <!-- Registration Details -->
        <div v-if="registrationDetail" class="details-section">
          <h3 class="details-heading">Registration Summary</h3>

          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-icon"><i class="fas fa-user"></i></span>
              <div>
                <span class="detail-label">Name</span>
                <span class="detail-value">{{ registrationDetail.name }}</span>
              </div>
            </div>

            <div class="detail-item">
              <span class="detail-icon"><i class="fas fa-phone"></i></span>
              <div>
                <span class="detail-label">Phone</span>
                <span class="detail-value">{{ registrationDetail.phoneNumber }}</span>
              </div>
            </div>

            <div class="detail-item">
              <span class="detail-icon"><i class="fas fa-users"></i></span>
              <div>
                <span class="detail-label">Participants</span>
                <span class="detail-value">
                  {{ registrationDetail.totalPerson }}
                  {{ registrationDetail.totalPerson === 1 ? 'Person' : 'People' }}
                </span>
              </div>
            </div>

            <div class="detail-item">
              <span class="detail-icon"><i class="fas fa-receipt"></i></span>
              <div>
                <span class="detail-label">Registration ID</span>
                <span class="detail-value mono">{{ shortId(registrationDetail._id) }}</span>
              </div>
            </div>

            <div class="detail-item">
              <span class="detail-icon"><i class="fas fa-rupee-sign"></i></span>
              <div>
                <span class="detail-label">Total Paid</span>
                <span class="detail-value price">
                  ₹{{ totalAmount }}
                </span>
              </div>
            </div>

            <div class="detail-item">
              <span class="detail-icon"><i class="fas fa-credit-card"></i></span>
              <div>
                <span class="detail-label">Payment Status</span>
                <span class="detail-value">
                  <span class="status-badge" :class="registrationDetail.paymentStatus">
                    <i class="fas" :class="registrationDetail.paymentStatus === 'paid' ? 'fa-check-circle' : 'fa-clock'"></i>
                    {{ capitalize(registrationDetail.paymentStatus) }}
                  </span>
                </span>
              </div>
            </div>

            <div v-if="registrationDetail.razorpayPaymentId" class="detail-item full-width">
              <span class="detail-icon"><i class="fas fa-barcode"></i></span>
              <div>
                <span class="detail-label">Payment ID</span>
                <span class="detail-value mono">{{ registrationDetail.razorpayPaymentId }}</span>
              </div>
            </div>

            <div v-if="registrationDetail.notes" class="detail-item full-width">
              <span class="detail-icon"><i class="fas fa-sticky-note"></i></span>
              <div>
                <span class="detail-label">Notes</span>
                <span class="detail-value">{{ registrationDetail.notes }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- What to Bring -->
        <div class="bring-section">
          <h3 class="details-heading">What to Expect</h3>
          <ul class="bring-list">
            <li><i class="fas fa-check-circle"></i> All materials & tools are provided</li>
            <li><i class="fas fa-check-circle"></i> Complimentary snacks & refreshments</li>
            <li><i class="fas fa-check-circle"></i> Wear comfortable clothes (art can be messy!)</li>
            <li><i class="fas fa-check-circle"></i> Arrive 10 minutes early</li>
          </ul>
        </div>

        <p class="confirmation-note">
          <i class="fas fa-envelope"></i>
          A confirmation has been sent to your registered contact.
        </p>

        <!-- Actions -->
        <div class="action-buttons">
          <button @click="$router.push('/workshops')" class="btn-secondary">
            <i class="fas fa-calendar-alt"></i>
            Browse More Workshops
          </button>
          <button @click="$router.push('/shop')" class="btn-primary">
            <i class="fas fa-shopping-bag"></i>
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductService from "../services/ProductService";
import { useToast } from 'vue-toast-notification';

export default {
  name: 'WorkshopSuccess',
  data() {
    return {
      workshopId: this.$route.query.workshopId,
      registrationId: this.$route.query.registrationId,
      workshopDetail: null,
      registrationDetail: null,
      isLoading: true
    }
  },
  computed: {
    totalAmount() {
      if (!this.workshopDetail || !this.registrationDetail) return 0;
      return parseInt(this.workshopDetail.price) * this.registrationDetail.totalPerson;
    }
  },
  methods: {
    async loadWorkshop() {
      const $toast = useToast();
      try {
        const response = await ProductService.loadWorkshop({
          workshopId: this.workshopId,
          registrationId: this.registrationId
        });
        this.workshopDetail = response.data.data.workshopDetail;
        this.registrationDetail = response.data.data.registrationDetail;
      } catch (error) {
        $toast.error('Failed to load registration details');
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    shortId(id) {
      if (!id) return '—';
      return '#' + id.toString().slice(-8).toUpperCase();
    },

    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  },
  mounted() {
    this.loadWorkshop();
    if (typeof AOS !== 'undefined') {
      AOS.init({ duration: 600 });
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

.success-page {
  min-height: 100vh;
  padding: 100px 20px 60px;
  background: #f8fafb;
  font-family: 'Inter', sans-serif;
}
.success-page .container {
  max-width: 720px !important;
  margin: 0 auto;
}

.success-card {
  background: white;
  border-radius: 24px;
  padding: 50px 40px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.1);
  /* Remove max-width: 640px — let container control width */
}

/* ── Loading ── */
.loading-card {
  text-align: center;
  padding: 60px;
  color: #718096;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top-color: rgb(118, 172, 175);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Card ── */
.success-card {
  background: white;
  border-radius: 24px;
  padding: 50px 40px;
  max-width: 640px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.1);
}

/* ── Icon ── */
.success-icon {
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, #48bb78, #38a169);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes scaleIn {
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}

.success-icon i {
  font-size: 2.5rem;
  color: white;
}

.success-title {
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.success-message {
  font-size: 1.05rem;
  color: #718096;
  margin-bottom: 30px;
  line-height: 1.6;
}

/* ── Workshop Banner ── */
.workshop-banner {
  background: linear-gradient(135deg, rgba(118,172,175,0.12), rgba(98,152,155,0.08));
  border: 1.5px solid rgba(118, 172, 175, 0.3);
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 28px;
  text-align: left;
}

.workshop-banner-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.workshop-banner-title i {
  color: rgb(118, 172, 175);
}

.workshop-banner-meta {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.workshop-banner-meta span,
.workshop-banner-address {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  font-size: 0.9rem;
}

.workshop-banner-meta i,
.workshop-banner-address i {
  color: rgb(118, 172, 175);
  width: 14px;
}

/* ── Details Grid ── */
.details-section {
  text-align: left;
  margin-bottom: 24px;
}

.details-heading {
  font-weight: 700;
  color: #2d3748;
  font-size: 1rem;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.85rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f8fafb;
  border-radius: 12px;
  padding: 14px 16px;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-icon {
  width: 32px;
  height: 32px;
  background: rgba(118, 172, 175, 0.15);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-icon i {
  color: rgb(118, 172, 175);
  font-size: 0.85rem;
}

.detail-item div {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.detail-label {
  font-size: 0.75rem;
  color: #a0aec0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  word-break: break-all;
}

.detail-value.mono {
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: #4a5568;
}

.detail-value.price {
  font-size: 1.2rem;
  color: rgb(98, 152, 155);
}

/* ── Status Badge ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
}

.status-badge.paid {
  background: rgba(72, 187, 120, 0.15);
  color: #38a169;
}

.status-badge.pending {
  background: rgba(237, 137, 54, 0.15);
  color: #c05621;
}

.status-badge.failed {
  background: rgba(245, 101, 101, 0.15);
  color: #c53030;
}

/* ── What to Expect ── */
.bring-section {
  text-align: left;
  background: #f8fafb;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
}

.bring-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bring-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4a5568;
  font-size: 0.9rem;
}

.bring-list i {
  color: #48bb78;
  flex-shrink: 0;
}

/* ── Confirmation Note ── */
.confirmation-note {
  color: #a0aec0;
  font-size: 0.88rem;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* ── Buttons ── */
.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 13px 26px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
}

.btn-secondary {
  background: white;
  color: rgb(118, 172, 175);
  border: 2px solid rgb(118, 172, 175);
}

.btn-secondary:hover {
  background: rgb(118, 172, 175);
  color: white;
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .success-card {
    padding: 36px 20px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-item.full-width {
    grid-column: 1;
  }

  .workshop-banner-meta {
    flex-direction: column;
    gap: 8px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
}
</style>